import AdAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useCtmAdCRUDActions } from '@mm/state/actions/CtmAdCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import { useRssFeedCRUDActions } from '@mm/state/actions/RssFeedCRUDActions';
import { useSponsoredMessageCRUDActions } from '@mm/state/actions/SponsoredMessageCRUDActions';
import {
  CampaignAudienceAttribute,
  CampaignSchedulerAttribute,
  CampaignType,
} from '@mm/state/model/CampaignsModel';
import { PageKind } from '@mm/state/model/PageModel';
import { SponsoredMessageModel } from '@mm/state/model/SponsoredMessageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectAdAccounts } from '@mm/state/selectors/adAccountsSelecotrs';
import { hasSendingChatBlastersSelector } from '@mm/state/selectors/campaignSelectors';
import { welcomePageSelector } from '@mm/state/selectors/onboardingSelectors';
import { selectFirstPage } from '@mm/state/selectors/pageSelectors';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
  activeFacebookPageIdSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { blasterCardTypes } from '../../../campaigns/components/BlastTypeSelector/blasterCardTypes';
import {
  QUEUED_BLAST_GUARD_MODAL_HEADER_TEXT,
  QUEUED_BLAST_GUARD_MODAL_TEXT,
} from '../../../campaigns/constants/messages';
import { DEFAULT_FEED_NAME } from '../../../rssFeeds/constants/texts';
import { UIFilterCampaignKindType } from './types';
import { useSyncedMessengerAdFactory } from './use-synced-messenger-factory';

const CONNECTED_PAGE_MISSING_ERROR = 'Bot should be connected to page';

const useFetchAdAccounts = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const adAccounts = useSelector(selectAdAccounts);
  const fbPageId = useSelector(activeFacebookPageIdSelector);

  const hasConnectedRealFacebookPage = useSelector(
    hasConnectedRealFacebookPageSelector,
  );

  const dispatch: Dispatch<StoreState> = useDispatch();
  const location = useLocation();
  const appropriateLocation =
    location.search.includes('ctm_ads') ||
    location.search.includes('sponsored_messages') ||
    location.search.includes('legacy_messenger_ads');

  useEffect(() => {
    if (
      (!adAccounts || adAccounts.length === 0) &&
      hasConnectedRealFacebookPage &&
      appropriateLocation
    ) {
      dispatch(AdAccountCRUDActions.retrieveAll(fbPageId, false, '', true));
    }
  }, [chatbotId]);

  return {
    adAccounts,
  };
};

const useCreateSponsoredMessage = () => {
  const router = useRouter();

  const adAccounts = useSelector(selectAdAccounts);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeChatbot = useSelector(activeChatbotSelector);

  const sponsoredMessageCRUDActions = useSponsoredMessageCRUDActions();

  let validationError;

  if (activeChatbot.template) {
    validationError = 'Cannot create a sponsored message on a template';
  }

  if (!adAccounts || adAccounts.length === 0) {
    validationError = 'Ad account missing';
  }

  const createSponsoredMessage = async (redirect = true) => {
    if (chatbotId) {
      const account = adAccounts[0];
      const entity: SponsoredMessageModel =
        await sponsoredMessageCRUDActions.create(chatbotId, {
          adAccountId: account.id,
          adAccountName: account.name,
          differentUser: account.differentUser,
        });

      if (redirect && entity?.id) {
        router.history.push(
          BotBuilderRoutes.sponsoredMessagesEdit.getLink({
            chatbotId,
            id: entity.id,
          }),
        );
      }
    }
  };

  return {
    createSponsoredMessage,
    validationError,
  };
};

export const useCreateCampaignHandlers = () => {
  const router = useRouter();

  const dispatch: Dispatch<StoreState> = useDispatch();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeChatbot = useSelector(activeChatbotSelector);
  const hasSendingChatBlasterCampaigns = useSelector(
    hasSendingChatBlastersSelector,
  );
  const connectedPage = useSelector(connectedPageSelector);

  const campaignsCRUDActions = useCampaignCRUDActions();

  const openQueuedBlastGuardModal = () =>
    dispatch(
      modalActions.openGeneralTextModal({
        header: QUEUED_BLAST_GUARD_MODAL_HEADER_TEXT,
        message: QUEUED_BLAST_GUARD_MODAL_TEXT,
      }),
    );

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const createChatBlaster = async () => {
    if (hasSendingChatBlasterCampaigns) {
      return openQueuedBlastGuardModal();
    }
    if (chatbotId) {
      const startBotOpts = {
        botId: chatbotId,
        name: 'Start',
        kind: PageKind.StartBot,
      };
      const dialogue = await dispatch(pageCRUDActions.create(startBotOpts));
      const dialogueId = dialogue?.page?.id;

      const payload = {
        botId: chatbotId,
        name: `Blast ${new Date().toLocaleString()}`,
        active: false,
        purpose: blasterCardTypes.promoMessage.purposeId,
        schedulersAttributes: [
          { destinationPageId: dialogueId } as CampaignSchedulerAttribute,
        ],
      } as CampaignType;

      if (!isUserPageConnected) {
        payload.platforms = [];
      }

      const campaign = await campaignsCRUDActions.createCampaign(payload);

      const campaignId = campaign?.id;

      if (dialogueId && campaignId) {
        router.history.push(
          BotBuilderRoutes.chatBlastEdit.getLink({
            chatbotId,
            campaignId,
            dialogueId,
          }),
        );
      }
    }
  };

  const createDripCampaign = async () => {
    if (chatbotId) {
      const payload = {
        src: 'pre_created_drip',
        scheduleType: 'drip',
        botId: chatbotId,
      } as CampaignType;

      if (!isUserPageConnected) {
        payload.platforms = [];
      }

      const campaign = await campaignsCRUDActions.createCampaign(payload);

      const campaignId = campaign?.id;

      if (campaignId) {
        router.history.push(
          BotBuilderRoutes.dripCampaignEdit.getLink({
            chatbotId,
            campaignId,
          }),
        );
      }
    }
  };

  const rssFeedCRUDActions = useRssFeedCRUDActions();
  const firstPage = useSelector(selectFirstPage);
  const welcomePage = useSelector(welcomePageSelector);
  const createRssBlast = async () => {
    const pageId = (welcomePage || firstPage)?.id || null;

    const feed = {
      name: DEFAULT_FEED_NAME,
      pageId,
    };

    const res = await rssFeedCRUDActions.create(chatbotId, feed);

    if (res && res.id) {
      const path = BotBuilderRoutes.rssFeed.getLink({
        chatbotId,
        rssFeedId: res.id,
      });
      router.history.push(path);
    }
  };

  const { adAccounts } = useFetchAdAccounts(); // sponsored message & messenger ads depend on ad accounts data

  const {
    createSponsoredMessage,
    validationError: sponsoredMessageValidationError,
  } = useCreateSponsoredMessage();

  const ctmAdCRUDActions = useCtmAdCRUDActions();
  const syncedMessengerAdFactory = useSyncedMessengerAdFactory({
    botId: chatbotId,
  });

  const createCtmAd = async () => {
    const ad = syncedMessengerAdFactory();
    const res = await ctmAdCRUDActions.create(chatbotId, {
      ...ad,
      adType: 'synced_messenger_ad',
    });
    if (res && res.id) {
      const path = BotBuilderRoutes.ctmAdsEdit.getLink({
        chatbotId,
        adId: res.id as number,
      });
      router.history.push(path);
    }
  };

  const createAutomation = (
    template?: string,
    templateParams?: any,
    botIdOverride?: number,
    campaignAudiencesAttributes?: CampaignAudienceAttribute[],
  ): Promise<CampaignType | undefined> => {
    const payload = {
      src: 'pre_created_automation',
      scheduleType: 'automation',
      botId: botIdOverride ? botIdOverride : chatbotId,
      campaignAudiencesAttributes,
    } as CampaignType;

    if (template) {
      (<any>payload).template = template;
      (<any>payload).templateParams = templateParams;
    }
    if (!isUserPageConnected) {
      payload.platforms = [];
    }

    return campaignsCRUDActions.createCampaign(payload);
  };

  const createByKind = (
    kind?: UIFilterCampaignKindType,
    template?: string,
    templateParams?: any,
    botIdOverride?: number,
    campaignAudiencesAttributes?: CampaignAudienceAttribute[],
  ) => {
    switch (kind) {
      case 'chat_blaster':
        return createChatBlaster();
      case 'drip_campaigns':
        return createDripCampaign();
      case 'rss_blasts':
        return createRssBlast();
      case 'sponsored_messages':
        return createSponsoredMessage();
      case 'ctm_ads':
        return createCtmAd();
      case 'automation':
        return createAutomation(
          template,
          templateParams,
          botIdOverride,
          campaignAudiencesAttributes,
        );
      default:
        throw new Error(
          `Missing implementation handler for campaign type: ${kind}`,
        );
    }
  };

  const connectedPageError = !connectedPage && CONNECTED_PAGE_MISSING_ERROR;
  const templateBotGuard = (name: string) =>
    !!activeChatbot.template && `Cannot create ${name} on a template`;
  const adAccountMissingError =
    !adAccounts || (adAccounts.length === 0 && 'Ad account missing');

  const validationErrorsMap: Partial<
    Record<UIFilterCampaignKindType, string | boolean>
  > = {
    automation: connectedPageError,
    drip_campaigns: connectedPageError,
    chat_blaster: connectedPageError || templateBotGuard('a chat blaster'),
    sponsored_messages: sponsoredMessageValidationError,
    ctm_ads:
      adAccountMissingError || templateBotGuard('a click-to-messenger ad'),
  };

  return {
    createByKind,
    validationErrorsMap,
  };
};
